import type {
  CalibrationHistory,
  AdjustmentReport,
  ModelInfo,
} from "./types.js";
import { getDefaultModels, getModelsByTier } from "./models.js";

interface GroupedEntries {
  taskType: string;
  complexity: string;
  model: string;
  avgAccuracy: number;
  count: number;
}

/**
 * Analyse calibration history and produce recommendations for
 * adjusting model routing preferences.
 *
 * Detects two patterns:
 *   1. **Overkill** — an expensive model is consistently accurate on a
 *      task type where a cheaper model would suffice (accuracy > 1.2 or
 *      a cheaper model has similar accuracy).
 *   2. **Underperformance** — a model consistently produces poor results
 *      for a task type (accuracy < 0.7 or > 1.5), suggesting escalation
 *      to a more capable model.
 */
export function adjustFromCalibration(
  calibration: CalibrationHistory,
  models?: ModelInfo[],
): AdjustmentReport {
  const registry = models ?? getDefaultModels();
  const adjustments: AdjustmentReport["adjustments"] = [];

  if (calibration.entries.length === 0) {
    return {
      adjustments: [],
      summary: "No calibration data available for adjustment analysis.",
    };
  }

  // Group entries by (model, taskType, complexity)
  const groups = groupEntries(calibration);

  for (const group of groups) {
    const model = registry.find((m) => m.id === group.model);
    if (!model) continue;

    // Need minimum 3 data points for confidence
    if (group.count < 3) continue;

    // ── Overkill detection ─────────────────────────────────
    // Model is frontier/mid but accuracy is very high — could downgrade
    if (
      (model.tier === "frontier" || model.tier === "mid") &&
      group.avgAccuracy >= 0.85 &&
      group.avgAccuracy <= 1.15
    ) {
      // See if a cheaper tier model exists with similar capabilities
      const cheaperTier =
        model.tier === "frontier" ? "mid" : "economy";
      const cheaperModels = getModelsByTier(cheaperTier, registry);

      if (cheaperModels.length > 0) {
        // Check if there's calibration data showing the cheaper model also works
        const cheaperWithData = cheaperModels.find((cm) => {
          const cmGroup = groups.find(
            (g) =>
              g.model === cm.id &&
              g.taskType === group.taskType &&
              g.complexity === group.complexity,
          );
          return cmGroup && cmGroup.avgAccuracy >= 0.8 && cmGroup.avgAccuracy <= 1.2;
        });

        if (cheaperWithData) {
          adjustments.push({
            taskType: group.taskType,
            complexity: group.complexity,
            currentModel: group.model,
            suggestedModel: cheaperWithData.id,
            reason: `${model.name} achieves ${(group.avgAccuracy * 100).toFixed(0)}% accuracy on ${group.taskType}/${group.complexity}, but ${cheaperWithData.name} also performs well at lower cost`,
            confidence: Math.min(0.9, group.count / 20),
            dataPoints: group.count,
          });
        } else {
          // No data on cheaper model — suggest trying it
          const suggestion = cheaperModels[0];
          if (suggestion) {
            adjustments.push({
              taskType: group.taskType,
              complexity: group.complexity,
              currentModel: group.model,
              suggestedModel: suggestion.id,
              reason: `${model.name} may be overkill for ${group.taskType}/${group.complexity} (${(group.avgAccuracy * 100).toFixed(0)}% accuracy). Consider trying ${suggestion.name} to reduce cost`,
              confidence: Math.min(0.6, group.count / 20),
              dataPoints: group.count,
            });
          }
        }
      }
    }

    // ── Underperformance detection ─────────────────────────
    // Model consistently inaccurate — suggest upgrade
    if (group.avgAccuracy < 0.7 || group.avgAccuracy > 1.5) {
      const betterTier =
        model.tier === "economy"
          ? "mid"
          : model.tier === "mid"
            ? "frontier"
            : null;

      if (betterTier) {
        const betterModels = getModelsByTier(
          betterTier as "frontier" | "mid" | "economy",
          registry,
        );
        const suggestion = betterModels[0];
        if (suggestion) {
          const direction =
            group.avgAccuracy < 0.7
              ? "underestimates work"
              : "overestimates work";
          adjustments.push({
            taskType: group.taskType,
            complexity: group.complexity,
            currentModel: group.model,
            suggestedModel: suggestion.id,
            reason: `${model.name} ${direction} on ${group.taskType}/${group.complexity} (${(group.avgAccuracy * 100).toFixed(0)}% accuracy). Escalate to ${suggestion.name}`,
            confidence: Math.min(0.85, group.count / 15),
            dataPoints: group.count,
          });
        }
      }
    }
  }

  // ── Summary ────────────────────────────────────────────────

  const downgrades = adjustments.filter((a) =>
    a.reason.includes("overkill") || a.reason.includes("also performs well"),
  ).length;
  const upgrades = adjustments.filter((a) =>
    a.reason.includes("Escalate"),
  ).length;

  let summary: string;
  if (adjustments.length === 0) {
    summary =
      "Current model routing is well-calibrated. No adjustments recommended.";
  } else {
    const parts: string[] = [];
    if (downgrades > 0)
      parts.push(`${downgrades} potential downgrade${downgrades > 1 ? "s" : ""} to save cost`);
    if (upgrades > 0)
      parts.push(`${upgrades} escalation${upgrades > 1 ? "s" : ""} for better accuracy`);
    summary = `Found ${adjustments.length} adjustment${adjustments.length > 1 ? "s" : ""}: ${parts.join(", ")}.`;
  }

  return { adjustments, summary };
}

// ── Helpers ──────────────────────────────────────────────────

function groupEntries(calibration: CalibrationHistory): GroupedEntries[] {
  const map = new Map<string, { total: number; count: number }>();

  for (const entry of calibration.entries) {
    const key = `${entry.model}|${entry.taskType}|${entry.complexity}`;
    const existing = map.get(key);
    if (existing) {
      existing.total += entry.accuracy;
      existing.count += 1;
    } else {
      map.set(key, { total: entry.accuracy, count: 1 });
    }
  }

  const groups: GroupedEntries[] = [];
  for (const [key, val] of map.entries()) {
    const [model, taskType, complexity] = key.split("|");
    groups.push({
      model: model!,
      taskType: taskType!,
      complexity: complexity!,
      avgAccuracy: val.total / val.count,
      count: val.count,
    });
  }
  return groups;
}
